import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { CreditCard, ArrowRight, Clock } from "@phosphor-icons/react";

/**
 * AddCardBanner — fires for no-card trial users in their final stretch.
 *
 * Renders ONLY when ALL of these are true:
 *   - User has an active trial
 *   - Trial was started WITHOUT a card (`has_payment_method === false`)
 *   - Days remaining ≤ 5
 *
 * The visual urgency scales with days remaining: amber from day-5 → day-3,
 * deeper rose from day-2 onward. Always non-blocking; user can scroll past.
 * Hides itself silently if `/api/trial/info` fails — analytics should never
 * block dashboard load.
 */

// Explicit Tailwind class maps — JIT can't compile interpolated class names.
const VARIANTS = {
  amber: {
    cardBg: "bg-gradient-to-br from-amber-500/8 via-zinc-950 to-zinc-950 border-amber-500/40 border-2",
    iconBox: "bg-amber-500/15 border-amber-500/40",
    iconColor: "text-amber-300",
    eyebrow: "text-amber-300",
    button: "bg-amber-500 hover:bg-amber-400",
  },
  rose: {
    cardBg: "bg-gradient-to-br from-rose-500/10 via-zinc-950 to-zinc-950 border-rose-500/50 border-2",
    iconBox: "bg-rose-500/15 border-rose-500/40",
    iconColor: "text-rose-300",
    eyebrow: "text-rose-300",
    button: "bg-rose-500 hover:bg-rose-400",
  },
};

const AddCardBanner = () => {
  const [trial, setTrial] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/trial/info`);
        if (alive) setTrial(res.data);
      } catch {
        // Silent
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!trial) return null;
  if (trial.trial_status !== "active") return null;
  if (trial.has_payment_method) return null;
  const daysLeft = Number(trial.days_remaining ?? 0);
  if (daysLeft > 5 || daysLeft < 0) return null;

  const isFinal = daysLeft <= 2;
  const v = isFinal ? VARIANTS.rose : VARIANTS.amber;
  const headline =
    daysLeft <= 1
      ? "Your trial ends today."
      : daysLeft <= 2
      ? `${Math.ceil(daysLeft)} days left — add a card today`
      : `${Math.ceil(daysLeft)} days left in your trial`;

  return (
    <Card className={v.cardBg} data-testid="add-card-banner">
      <CardContent className="p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className={`shrink-0 w-12 h-12 rounded-sm flex items-center justify-center ${v.iconBox}`}>
            {isFinal ? (
              <Clock weight="duotone" className={`w-6 h-6 ${v.iconColor}`} />
            ) : (
              <CreditCard weight="duotone" className={`w-6 h-6 ${v.iconColor}`} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-[10px] font-bold uppercase tracking-[0.18em] mb-1 ${v.eyebrow}`}>
              No-card trial · ends Day 15
            </div>
            <h3 className="text-lg font-bold text-white leading-tight" data-testid="add-card-headline">
              {headline}
            </h3>
            <p className="text-sm text-zinc-400 mt-1 leading-snug">
              You started your 14-day trial without a card &mdash; nice. To keep <b className="text-white">Mission Diagnostic</b>, <b className="text-white">unlimited Flash Drafts</b>, <b className="text-white">Corporate Scout</b>, and the rest of Premium going past Day 15, add a card now. Otherwise your account drops to Free automatically.
            </p>
          </div>
          <Link to="/pricing" className="shrink-0 w-full sm:w-auto">
            <Button
              className={`w-full sm:w-auto text-black font-bold px-5 ${v.button}`}
              data-testid="add-card-cta-btn"
            >
              Add a card
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

export default AddCardBanner;
